import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  VStack,
  HStack,
  useToast,
  Spinner,
  Center,
  Progress,
  TableCaption,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { getBackendURL } from "./env";

function FunctionDataMonitor() {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const backendUrl = getBackendURL();
      const response = await fetch(`${backendUrl}/api/function-data-monitor/`, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Error fetching function data:", err);
      setError(err.message || "Failed to fetch function data");
      toast({
        title: "Error",
        description: err.message || "Failed to fetch function data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    let unitIndex = 0;
    let value = bytes;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    return `${value.toFixed(2)} ${units[unitIndex]}`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return "N/A";
    return num.toLocaleString();
  };

  if (isLoading && !data) {
    return (
      <Center py={4}>
        <Spinner size="md" />
      </Center>
    );
  }

  const functionData = data?.function_data_usage || [];
  const detailedStats = data?.detailed_function_stats || [];
  const tableMemory = data?.table_memory_usage || [];
  const indexMemory = data?.index_memory_usage || [];
  const queryMemory = data?.query_memory_usage || [];
  const schemaMemory = data?.schema_memory_summary || [];

  // NEW: Storage data (actual disk usage)
  const tableStorage = data?.table_storage_size || [];
  const schemaStorage = data?.schema_storage_summary || [];
  const indexStorage = data?.index_storage_size || [];
  const totalDbSize = data?.total_database_size || {};

  // Memory info (PostgreSQL RAM configuration)
  const memoryInfo = data?.memory_info || [];
  const memoryTotalMb = data?.memory_total_mb || 0;

  // Calculate totals
  const totalCalls = functionData.reduce(
    (sum, f) => sum + (f.total_calls || 0),
    0,
  );
  const totalData = functionData.reduce(
    (sum, f) => sum + (f.total_data_bytes || 0),
    0,
  );

  // Calculate memory stats (buffer cache)
  const totalTableMemory = tableMemory.reduce(
    (sum, t) => sum + (t.total_mb || 0),
    0,
  );
  const totalQueryMemory = queryMemory.reduce(
    (sum, q) => sum + (q.mem_mb || 0),
    0,
  );

  // Calculate storage totals (actual disk usage)
  const totalTableStorage = tableStorage.reduce(
    (sum, t) => sum + (t.size_bytes || 0),
    0,
  );
  const totalIndexStorage = indexStorage.reduce(
    (sum, i) => sum + (i.size_bytes || 0),
    0,
  );
  const totalStorage =
    totalDbSize?.total_bytes || totalTableStorage + totalIndexStorage;

  // Priority: show RAM if available, otherwise show storage
  const headerBadge =
    memoryTotalMb > 0
      ? {
          label: `${memoryTotalMb.toFixed(0)} MB RAM`,
          color:
            memoryTotalMb > 512
              ? "red"
              : memoryTotalMb > 256
                ? "orange"
                : "green",
        }
      : {
          label: formatBytes(totalStorage),
          color:
            totalStorage > 1000000000
              ? "red"
              : totalStorage > 500000000
                ? "orange"
                : "green",
        };

  return (
    <Card>
      <CardHeader>
        <HStack justify="space-between">
          <Heading size="md">Database Storage & Function Usage</Heading>
          <HStack spacing={2}>
            <Badge colorScheme={totalCalls > 0 ? "blue" : "gray"} fontSize="md">
              {formatNumber(totalCalls)} calls
            </Badge>
            <Badge colorScheme={headerBadge.color} fontSize="md">
              {headerBadge.label}
            </Badge>
          </HStack>
        </HStack>
      </CardHeader>
      <CardBody>
        <Accordion allowMultiple defaultIndex={[0, 1]}>
          {/* MEMORY USAGE CHART - PostgreSQL RAM configuration and usage */}
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              _hover={{ bg: "gray.50" }}
              borderRadius="md"
            >
              <Box flex="1" textAlign="left">
                <Heading size="sm">
                  💻 Memory Usage (PostgreSQL RAM Configuration)
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={4}>
              {memoryInfo.length > 0 ? (
                <>
                  <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4} mb={4}>
                    {memoryInfo.map((mem, idx) => {
                      const isTotal = mem.category === "Calculated Total";
                      return (
                        <Stat key={idx}>
                          <StatLabel>{mem.category}</StatLabel>
                          <StatNumber
                            color={isTotal ? "red.500" : "blue.500"}
                            fontSize={isTotal ? "xl" : "md"}
                          >
                            {mem.value}
                          </StatNumber>
                          <StatHelpText>{mem.description}</StatHelpText>
                        </Stat>
                      );
                    })}
                  </SimpleGrid>

                  {/* Memory Bar Chart Visualization */}
                  <Box mb={4}>
                    <Text fontWeight="bold" mb={2} fontSize="sm">
                      Memory Allocation
                    </Text>
                    <VStack spacing={2} align="stretch">
                      {memoryInfo.slice(0, 5).map((mem, idx) => {
                        let maxBytes = 0;
                        for (const m of memoryInfo) {
                          if (m.category === "Calculated Total") {
                            maxBytes = m.bytes;
                            break;
                          }
                        }
                        const pct =
                          maxBytes > 0 ? (mem.bytes / maxBytes) * 100 : 0;
                        const barColor = mem.category.includes("Shared")
                          ? "blue"
                          : mem.category.includes("Work")
                            ? "orange"
                            : mem.category.includes("Cache")
                              ? "purple"
                              : "gray";
                        return (
                          <Box key={idx}>
                            <Flex justify="space-between" mb={1}>
                              <Text fontSize="sm" fontWeight="medium">
                                {mem.category}
                              </Text>
                              <Text fontSize="sm" color="gray.500">
                                {mem.value}
                              </Text>
                            </Flex>
                            <Progress
                              value={pct}
                              colorScheme={barColor}
                              size="sm"
                              borderRadius="md"
                            />
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>

                  {/* Memory Summary */}
                  <Box
                    p={3}
                    bg="red.50"
                    borderRadius="md"
                    borderLeft="4px solid"
                    borderLeftColor="red.500"
                  >
                    <Text fontWeight="bold" color="red.600">
                      Total PostgreSQL Memory: {memoryTotalMb.toFixed(0)} MB
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      This includes shared_buffers + estimated OS cache. Used
                      memory depends on active queries and connection count.
                    </Text>
                  </Box>
                </>
              ) : (
                <Text color="gray.500" fontSize="sm">
                  No memory info available. Run the SQL to add get_memory_info
                  function.
                </Text>
              )}
            </AccordionPanel>
          </AccordionItem>

          {/* ACTUAL STORAGE (DISK USAGE) - This shows the real database size */}
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              _hover={{ bg: "gray.50" }}
              borderRadius="md"
            >
              <Box flex="1" textAlign="left">
                <Heading size="sm">
                  💾 Database Storage (Actual disk usage - what's taking space)
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={4}>
              {/* Storage Summary Stats */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
                <Stat>
                  <StatLabel>Total Database Size</StatLabel>
                  <StatNumber color="red.500">
                    {formatBytes(totalStorage)}
                  </StatNumber>
                  <StatHelpText>Actual disk space used</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Table Data</StatLabel>
                  <StatNumber color="blue.500">
                    {formatBytes(totalTableStorage)}
                  </StatNumber>
                  <StatHelpText>Table row data</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Index Data</StatLabel>
                  <StatNumber color="purple.500">
                    {formatBytes(totalIndexStorage)}
                  </StatNumber>
                  <StatHelpText>Index sizes</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Schemas</StatLabel>
                  <StatNumber>{schemaStorage.length}</StatNumber>
                  <StatHelpText>With storage</StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* Schema Storage Summary */}
              {schemaStorage.length > 0 && (
                <Box mb={4}>
                  <Text fontWeight="bold" mb={2} fontSize="sm">
                    Storage by Schema (Actual disk usage)
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {schemaStorage.map((schema, idx) => (
                      <Box
                        key={idx}
                        p={2}
                        borderRadius="md"
                        bg={
                          schema.total_bytes > 100000000 ? "red.50" : "gray.50"
                        }
                        borderLeft="3px solid"
                        borderLeftColor={
                          schema.total_bytes > 100000000
                            ? "red.500"
                            : schema.total_bytes > 10000000
                              ? "orange.500"
                              : "green.500"
                        }
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {schema.schema_name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {schema.table_count} tables, {schema.index_count}{" "}
                              indexes
                            </Text>
                          </VStack>
                          <VStack align="end" spacing={0}>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color="red.500"
                            >
                              {formatBytes(schema.total_bytes || 0)}
                            </Text>
                            <Badge size="xs" colorScheme="gray">
                              disk
                            </Badge>
                          </VStack>
                        </Flex>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              {/* Table Storage Usage */}
              {tableStorage.length > 0 ? (
                <Box mb={4}>
                  <Text fontWeight="bold" mb={2} fontSize="sm">
                    Table Storage (Actual disk usage per table)
                  </Text>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Table</Th>
                          <Th>Schema</Th>
                          <Th isNumeric>Size</Th>
                          <Th isNumeric>Indexes</Th>
                          <Th isNumeric>Total</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {tableStorage.slice(0, 15).map((tbl, idx) => (
                          <Tr
                            key={idx}
                            bg={
                              tbl.size_bytes > 10000000
                                ? "red.50"
                                : tbl.size_bytes > 1000000
                                  ? "orange.50"
                                  : "inherit"
                            }
                          >
                            <Td>
                              <Text fontSize="sm" fontWeight="medium">
                                {tbl.table_name}
                              </Text>
                            </Td>
                            <Td>
                              <Badge size="sm">{tbl.schema_name}</Badge>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                color="blue.500"
                                fontWeight="bold"
                              >
                                {formatBytes(tbl.size_bytes || 0)}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color="purple.500">
                                {formatBytes(tbl.indexes_size_bytes || 0)}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={
                                  tbl.total_bytes > 10000000
                                    ? "red.500"
                                    : tbl.total_bytes > 1000000
                                      ? "orange.500"
                                      : "inherit"
                                }
                              >
                                {formatBytes(tbl.total_bytes || 0)}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              ) : (
                <Text color="gray.500" fontSize="sm">
                  No table storage data available
                </Text>
              )}

              {/* Index Storage Usage */}
              {indexStorage.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2} fontSize="sm">
                    Index Storage (What indexes are using)
                  </Text>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Index</Th>
                          <Th>Table</Th>
                          <Th>Schema</Th>
                          <Th isNumeric>Size</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {indexStorage.slice(0, 10).map((idx, i) => (
                          <Tr
                            key={i}
                            bg={
                              idx.size_bytes > 1000000 ? "purple.50" : "inherit"
                            }
                          >
                            <Td>
                              <Text fontSize="sm" fontFamily="mono">
                                {idx.index_name}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{idx.table_name}</Text>
                            </Td>
                            <Td>
                              <Badge size="sm">{idx.schema_name}</Badge>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={
                                  idx.size_bytes > 1000000
                                    ? "purple.500"
                                    : "inherit"
                                }
                              >
                                {formatBytes(idx.size_bytes || 0)}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </AccordionPanel>
          </AccordionItem>

          {/* RAM/Memory Usage Section (Buffer Cache - I/O) */}
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              _hover={{ bg: "gray.50" }}
              borderRadius="md"
            >
              <Box flex="1" textAlign="left">
                <Heading size="sm">
                  🧠 Buffer Cache I/O (Memory used for caching)
                </Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={4}>
              {/* Memory Summary Stats */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
                <Stat>
                  <StatLabel>Table Cache Memory</StatLabel>
                  <StatNumber color="blue.500">
                    {totalTableMemory.toFixed(1)} MB
                  </StatNumber>
                  <StatHelpText>Buffer cache used</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Query Memory</StatLabel>
                  <StatNumber color="orange.500">
                    {totalQueryMemory.toFixed(1)} MB
                  </StatNumber>
                  <StatHelpText>Active query usage</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Tables Tracked</StatLabel>
                  <StatNumber>{tableMemory.length}</StatNumber>
                  <StatHelpText>With memory usage</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Schemas</StatLabel>
                  <StatNumber>{schemaMemory.length}</StatNumber>
                  <StatHelpText>With memory usage</StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* Table Memory Usage */}
              {tableMemory.length > 0 ? (
                <Box mb={4}>
                  <Text fontWeight="bold" mb={2} fontSize="sm">
                    Table Memory Consumption (Buffer Cache)
                  </Text>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Table</Th>
                          <Th>Schema</Th>
                          <Th isNumeric>Heap Read</Th>
                          <Th isNumeric>Idx Read</Th>
                          <Th isNumeric>Total MB</Th>
                          <Th isNumeric>Cache Hit %</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {tableMemory.slice(0, 15).map((tbl, idx) => (
                          <Tr
                            key={idx}
                            bg={
                              tbl.total_mb > 10
                                ? "red.50"
                                : tbl.total_mb > 5
                                  ? "orange.50"
                                  : "inherit"
                            }
                          >
                            <Td>
                              <Text fontSize="sm" fontWeight="medium">
                                {tbl.table_name}
                              </Text>
                            </Td>
                            <Td>
                              <Badge size="sm">{tbl.schema_name}</Badge>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color="blue.500">
                                {tbl.heap_mb_read?.toFixed(2) || 0} MB
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color="purple.500">
                                {tbl.idx_mb_read?.toFixed(2) || 0} MB
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={
                                  tbl.total_mb > 10
                                    ? "red.500"
                                    : tbl.total_mb > 5
                                      ? "orange.500"
                                      : "inherit"
                                }
                              >
                                {tbl.total_mb?.toFixed(2) || 0} MB
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Badge
                                colorScheme={
                                  tbl.cache_hit_ratio >= 90
                                    ? "green"
                                    : tbl.cache_hit_ratio >= 70
                                      ? "orange"
                                      : "red"
                                }
                              >
                                {tbl.cache_hit_ratio?.toFixed(1) || 0}%
                              </Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              ) : (
                <Text color="gray.500" fontSize="sm">
                  No table memory data available
                </Text>
              )}

              {/* Schema Memory Summary */}
              {schemaMemory.length > 0 && (
                <Box mb={4}>
                  <Text fontWeight="bold" mb={2} fontSize="sm">
                    Schema Memory Summary
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    {schemaMemory.map((schema, idx) => (
                      <Box
                        key={idx}
                        p={2}
                        borderRadius="md"
                        bg={schema.total_mb > 10 ? "red.50" : "gray.50"}
                        borderLeft="3px solid"
                        borderLeftColor={
                          schema.total_mb > 10
                            ? "red.500"
                            : schema.total_mb > 5
                              ? "orange.500"
                              : "green.500"
                        }
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {schema.schema_name}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {schema.table_count} tables
                            </Text>
                          </VStack>
                          <VStack align="end" spacing={0}>
                            <Text fontSize="sm" fontWeight="bold">
                              {schema.total_mb?.toFixed(1) || 0} MB
                            </Text>
                            <Badge
                              size="xs"
                              colorScheme={
                                schema.avg_cache_hit_ratio >= 90
                                  ? "green"
                                  : "orange"
                              }
                            >
                              {schema.avg_cache_hit_ratio?.toFixed(0) || 0}%
                              cache
                            </Badge>
                          </VStack>
                        </Flex>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}

              {/* Query Memory Usage */}
              {queryMemory.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2} fontSize="sm">
                    Active Query Memory (Current RAM Usage)
                  </Text>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>PID</Th>
                          <Th>User</Th>
                          <Th>State</Th>
                          <Th>Query Type</Th>
                          <Th isNumeric>Mem MB</Th>
                          <Th isNumeric>Temp MB</Th>
                          <Th isNumeric>Duration</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {queryMemory.slice(0, 10).map((q, idx) => (
                          <Tr
                            key={idx}
                            bg={
                              q.mem_mb > 50
                                ? "red.50"
                                : q.mem_mb > 10
                                  ? "orange.50"
                                  : "inherit"
                            }
                          >
                            <Td>
                              <Text fontSize="sm" fontFamily="mono">
                                {q.pid}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{q.usename}</Text>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  q.state === "active" ? "green" : "yellow"
                                }
                                size="sm"
                              >
                                {q.state}
                              </Badge>
                            </Td>
                            <Td>
                              <Badge
                                colorScheme={
                                  q.query_type === "SELECT"
                                    ? "blue"
                                    : q.query_type === "INSERT"
                                      ? "green"
                                      : q.query_type === "UPDATE"
                                        ? "orange"
                                        : "gray"
                                }
                                size="sm"
                              >
                                {q.query_type}
                              </Badge>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                fontWeight="bold"
                                color={
                                  q.mem_mb > 50
                                    ? "red.500"
                                    : q.mem_mb > 10
                                      ? "orange.500"
                                      : "inherit"
                                }
                              >
                                {q.mem_mb?.toFixed(2) || 0}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                color={
                                  q.temp_mb > 10 ? "purple.500" : "gray.500"
                                }
                              >
                                {q.temp_mb?.toFixed(2) || 0}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text
                                fontSize="sm"
                                color={
                                  q.duration_seconds > 60
                                    ? "red.500"
                                    : "inherit"
                                }
                              >
                                {q.duration_seconds}s
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </AccordionPanel>
          </AccordionItem>

          {/* Function Data Section */}
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              _hover={{ bg: "gray.50" }}
              borderRadius="md"
            >
              <Box flex="1" textAlign="left">
                <Heading size="sm">📊 Function Data Usage</Heading>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={4}>
              {/* Summary Stats */}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
                <Stat>
                  <StatLabel>Total Functions</StatLabel>
                  <StatNumber>{functionData.length}</StatNumber>
                  <StatHelpText>Tracked functions</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Total Calls</StatLabel>
                  <StatNumber color="blue.500">
                    {formatNumber(totalCalls)}
                  </StatNumber>
                  <StatHelpText>All function calls</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Total Data</StatLabel>
                  <StatNumber color="green.500">
                    {formatBytes(totalData)}
                  </StatNumber>
                  <StatHelpText>Approx. data sent</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Avg Duration</StatLabel>
                  <StatNumber>
                    {functionData.length > 0
                      ? (
                          functionData.reduce(
                            (sum, f) => sum + (f.avg_duration_ms || 0),
                            0,
                          ) / functionData.length
                        ).toFixed(2)
                      : 0}
                    ms
                  </StatNumber>
                  <StatHelpText>Average execution time</StatHelpText>
                </Stat>
              </SimpleGrid>

              {/* Function Data Table */}
              {functionData.length > 0 ? (
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <TableCaption>
                      Top functions sending data to Supabase
                    </TableCaption>
                    <Thead>
                      <Tr>
                        <Th>Function Name</Th>
                        <Th isNumeric>Calls</Th>
                        <Th isNumeric>Rows Affected</Th>
                        <Th isNumeric>Data Sent</Th>
                        <Th isNumeric>Avg Duration</Th>
                        <Th>Last Called</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {functionData.map((func, idx) => (
                        <Tr key={idx}>
                          <Td>
                            <Text fontWeight="medium" fontSize="sm">
                              {func.function_name}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Badge
                              colorScheme={
                                func.total_calls > 1000
                                  ? "red"
                                  : func.total_calls > 100
                                    ? "orange"
                                    : "green"
                              }
                            >
                              {formatNumber(func.total_calls)}
                            </Badge>
                          </Td>
                          <Td isNumeric>
                            <Text fontSize="sm">
                              {formatNumber(func.total_rows_affected)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text
                              fontSize="sm"
                              color={
                                func.total_data_bytes > 1000000
                                  ? "red.500"
                                  : func.total_data_bytes > 100000
                                    ? "orange.500"
                                    : "green.500"
                              }
                              fontWeight="medium"
                            >
                              {formatBytes(func.total_data_bytes)}
                            </Text>
                          </Td>
                          <Td isNumeric>
                            <Text
                              fontSize="sm"
                              color={
                                func.avg_duration_ms > 100
                                  ? "orange.500"
                                  : "inherit"
                              }
                            >
                              {func.avg_duration_ms?.toFixed(2) || 0} ms
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="xs" color="gray.500">
                              {func.last_called
                                ? new Date(func.last_called).toLocaleString()
                                : "N/A"}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text color="gray.500" textAlign="center" py={4}>
                  No function data available. Run the SQL to add monitoring
                  functions.
                </Text>
              )}

              {/* Detailed Function Stats */}
              {detailedStats.length > 0 && (
                <>
                  <Heading size="sm" mt={6} mb={3}>
                    Detailed Function Statistics
                  </Heading>
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Function</Th>
                          <Th isNumeric>Calls</Th>
                          <Th isNumeric>Total Time (s)</Th>
                          <Th isNumeric>Avg Time (ms)</Th>
                          <Th isNumeric>Calls/Sec</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {detailedStats.slice(0, 10).map((func, idx) => (
                          <Tr key={idx}>
                            <Td>
                              <VStack align="start" spacing={0}>
                                <Text fontSize="sm" fontWeight="medium">
                                  {func.function_name}
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                  {func.function_schema}
                                </Text>
                              </VStack>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm">
                                {formatNumber(func.total_calls)}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color="blue.500">
                                {(func.total_time_ms / 1000).toFixed(2)}s
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm">
                                {func.avg_time_ms?.toFixed(2) || 0}
                              </Text>
                            </Td>
                            <Td isNumeric>
                              <Text fontSize="sm" color="green.500">
                                {func.calls_per_second?.toFixed(2) || 0}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </>
              )}
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </CardBody>
    </Card>
  );
}

export default FunctionDataMonitor;
